import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/primitives/Dialog';
import { SUBJECT_CATALOG } from '../constants/subjectCatalog';
import {
  STUDIO_CREATE_RESOURCE_OPTIONS,
  flowProfileOptionsForSubject,
  type StudioCreateResourceType,
  type StudioFlowProfile,
} from '../constants/studioCreateConstants';
import { buildStudioCreateNavigation } from '../lib/studioCreateNavigation';
import { getCourseProfiles } from '../services/courseProfileStorage';
import { buildMaterialSeedFromLinkedSession } from '../types/linkedSessionContext';
import type { LinkedSessionContext } from '../types/linkedSessionContext';
import type { CourseProfile } from '../types/courseProfile';
import { resolveCourseDisplayName } from '../constants/courseProfileUi';

const WIZARD_STEPS = ['tipo', 'intencion', 'flujo'] as const;
type WizardStep = (typeof WIZARD_STEPS)[number];

export interface StudioCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Curso activo — inyecta contexto en silencio (paso 2). */
  course?: CourseProfile | null;
  subjectId?: string;
  subjectLabel?: string;
  linkedSessionContext?: LinkedSessionContext;
  /** Abrir directamente en un paso (p. ej. planificación → paso 2). */
  initialStep?: WizardStep;
  defaultResourceType?: StudioCreateResourceType;
}

export function StudioCreateModal({
  open,
  onOpenChange,
  course = null,
  subjectId: subjectIdProp,
  subjectLabel,
  linkedSessionContext,
  initialStep = 'tipo',
  defaultResourceType,
}: StudioCreateModalProps) {
  const navigate = useNavigate();
  const stepHeadingId = useId();
  const wasOpenRef = useRef(false);
  const [step, setStep] = useState<WizardStep>(initialStep);
  const [resourceType, setResourceType] = useState<StudioCreateResourceType | null>(
    defaultResourceType ?? null,
  );
  const [teacherPrompt, setTeacherPrompt] = useState('');
  const [flowProfile, setFlowProfile] = useState<StudioFlowProfile>('generic');
  const [subjectId, setSubjectId] = useState(
    subjectIdProp ?? course?.subjectId ?? 'matematica',
  );
  const [pickedCourseId, setPickedCourseId] = useState('');

  const availableCourses = useMemo(() => (open ? getCourseProfiles() : []), [open]);

  const effectiveCourse = useMemo(() => {
    if (course) return course;
    if (!pickedCourseId) return null;
    return availableCourses.find((item) => item.id === pickedCourseId) ?? null;
  }, [availableCourses, course, pickedCourseId]);

  const resolvedSubjectId = effectiveCourse?.subjectId ?? subjectId;
  const flowProfileOptions = useMemo(
    () => flowProfileOptionsForSubject(effectiveCourse?.subjectTag ?? resolvedSubjectId),
    [effectiveCourse?.subjectTag, resolvedSubjectId],
  );

  useEffect(() => {
    if (!flowProfileOptions.some((option) => option.id === flowProfile)) {
      setFlowProfile('generic');
    }
  }, [flowProfile, flowProfileOptions]);

  const resetWizard = useCallback(() => {
    setStep(initialStep);
    setResourceType(defaultResourceType ?? null);
    setFlowProfile('generic');
    setSubjectId(subjectIdProp ?? course?.subjectId ?? 'matematica');
    setPickedCourseId('');
    // Solo prefills desde sesión planificada. El curso aporta nivel/asignatura
    // por payload al backend — no se escribe en el textarea.
    if (linkedSessionContext) {
      setTeacherPrompt(buildMaterialSeedFromLinkedSession(linkedSessionContext));
    } else {
      setTeacherPrompt('');
    }
  }, [course?.subjectId, defaultResourceType, initialStep, linkedSessionContext, subjectIdProp]);

  // Solo reset al abrir — evita que un re-render del padre borre la selección del paso 1.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      resetWizard();
    }
    wasOpenRef.current = open;
  }, [open, resetWizard]);

  const stepIndex = WIZARD_STEPS.indexOf(step);
  const needsCoursePick = resourceType === 'rubric' && !course;

  const canAdvance = useMemo(() => {
    if (step === 'tipo') return resourceType !== null;
    if (step === 'intencion') {
      if (teacherPrompt.trim().length < 8) return false;
      if (needsCoursePick) return Boolean(pickedCourseId);
      return true;
    }
    if (step === 'flujo') return Boolean(flowProfile);
    return false;
  }, [flowProfile, needsCoursePick, pickedCourseId, resourceType, step, teacherPrompt]);

  const goNext = () => {
    if (!canAdvance) return;
    if (step === 'tipo') setStep('intencion');
    else if (step === 'intencion') setStep('flujo');
  };

  const goBack = () => {
    if (step === 'flujo') setStep('intencion');
    else if (step === 'intencion') setStep('tipo');
  };

  const selectResourceType = (id: StudioCreateResourceType) => {
    setResourceType(id);
    // Avance inmediato al elegir tipo (evita quedar atrapado si el footer queda fuera de vista).
    setStep('intencion');
  };

  const handleComplete = () => {
    if (!resourceType || !canAdvance) return;
    if (resourceType === 'rubric' && !effectiveCourse) return;

    const { path, state } = buildStudioCreateNavigation({
      resourceType,
      teacherPrompt,
      flowProfile,
      course: effectiveCourse,
      subjectId: resolvedSubjectId,
      linkedSessionContext,
    });

    onOpenChange(false);
    navigate(path, state ? { state } : undefined);
  };

  const stepTitle =
    step === 'tipo'
      ? '¿Qué vas a crear?'
      : step === 'intencion'
        ? '¿Qué quieres construir?'
        : 'Cómo se desarrolla la clase';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="studio-create-modal flex max-h-[min(92dvh,44rem)] w-[min(calc(100vw-1.5rem),32rem)] flex-col overflow-hidden rounded-studio-2xl border-0 p-0 shadow-studio-lg"
        aria-modal="true"
        aria-labelledby={stepHeadingId}
        showClose
      >
        <div className="studio-create-modal__scrim shrink-0 border-b border-studio-border px-6 pb-4 pt-6 backdrop-blur-sm">
          <DialogHeader className="space-y-1 text-left">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-studio-ink-tertiary">
              Paso {stepIndex + 1} de {WIZARD_STEPS.length}
              {subjectLabel ? ` · ${subjectLabel}` : ''}
            </p>
            <DialogTitle id={stepHeadingId} className="font-display text-xl font-bold text-studio-ink">
              {stepTitle}
            </DialogTitle>
            <DialogDescription className="text-sm text-studio-ink-secondary">
              {step === 'tipo'
                ? 'Elige el formato del recurso antes de generar con IA.'
                : step === 'intencion'
                  ? course
                    ? 'Cuéntale a la IA qué material necesitas. Nivel y asignatura del curso se aplican solos.'
                    : needsCoursePick
                      ? 'Elige el curso donde vivirá la rúbrica y describe qué quieres evaluar.'
                      : 'Describe qué quieres lograr en clase con este material.'
                  : 'Elige la dinámica de la clase. Esto guía cómo se estructura el material.'}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="studio-create-modal__body min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {step === 'tipo' ? (
            <div className="flex flex-col gap-2" role="listbox" aria-label="Tipo de recurso">
              {STUDIO_CREATE_RESOURCE_OPTIONS.map((option) => {
                const Icon = option.Icon;
                const selected = resourceType === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    data-selected={selected ? 'true' : undefined}
                    className="studio-create-option flex w-full items-start gap-4 rounded-studio-xl p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-studio-accent-ring"
                    onClick={() => selectResourceType(option.id)}
                  >
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-studio-md"
                      style={{
                        background:
                          option.accent === 'mint'
                            ? 'var(--studio-accent-mint-subtle, #dceee0)'
                            : 'var(--studio-accent-subtle, #f5d4c8)',
                        color:
                          option.accent === 'mint'
                            ? 'var(--studio-accent-mint, #5f8a68)'
                            : 'var(--studio-accent, #e07a5f)',
                      }}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-display text-sm font-bold text-studio-ink">
                        {option.title}
                      </span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-studio-ink-secondary">
                        {option.subtitle}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {step === 'intencion' ? (
            <div className="space-y-4">
              {course ? (
                <p className="rounded-studio-md border border-studio-border bg-studio-surface-muted px-3 py-2 text-xs text-studio-ink-secondary">
                  <Sparkles className="mr-1 inline h-3.5 w-3.5 text-studio-accent" aria-hidden />
                  El curso activo aporta nivel, asignatura e intereses a la generación — no hace falta
                  repetirlos aquí.
                </p>
              ) : needsCoursePick ? (
                <label className="block text-xs font-medium text-studio-ink-secondary">
                  Curso
                  <select
                    className="studio-create-field mt-1.5 w-full rounded-studio-md border border-studio-border bg-studio-surface-muted px-3 py-2.5 text-sm text-studio-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-studio-accent-ring"
                    value={pickedCourseId}
                    onChange={(event) => setPickedCourseId(event.target.value)}
                  >
                    <option value="">Elegir curso…</option>
                    {availableCourses.map((item) => (
                      <option key={item.id} value={item.id}>
                        {resolveCourseDisplayName(item)}
                      </option>
                    ))}
                  </select>
                  {availableCourses.length === 0 ? (
                    <span className="mt-1.5 block text-[11px] text-studio-ink-tertiary">
                      Aún no hay cursos. Crea uno desde Mis cursos y vuelve a construir la rúbrica.
                    </span>
                  ) : null}
                </label>
              ) : (
                <label className="block text-xs font-medium text-studio-ink-secondary">
                  Asignatura
                  <select
                    className="studio-create-field mt-1.5 w-full rounded-studio-md border border-studio-border bg-studio-surface-muted px-3 py-2.5 text-sm text-studio-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-studio-accent-ring"
                    value={subjectId}
                    onChange={(event) => setSubjectId(event.target.value)}
                  >
                    {SUBJECT_CATALOG.filter((s) => s.id !== 'general').map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="block text-xs font-medium text-studio-ink-secondary">
                Qué quieres construir
                <textarea
                  className="studio-create-field mt-1.5 min-h-[9rem] w-full resize-y rounded-studio-md border border-studio-border bg-studio-surface-muted px-3 py-3 text-sm leading-relaxed text-studio-ink placeholder:text-studio-ink-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-studio-accent-ring"
                  placeholder="Ej.: Una guía para practicar fracciones equivalentes con ejemplos del patio y un ticket de salida…"
                  value={teacherPrompt}
                  onChange={(event) => setTeacherPrompt(event.target.value)}
                  aria-describedby="studio-create-prompt-hint"
                />
              </label>
              <p id="studio-create-prompt-hint" className="text-[11px] text-studio-ink-tertiary">
                Mínimo 8 caracteres. Sé concreto: tema, producto o actividad que esperas.
              </p>
            </div>
          ) : null}

          {step === 'flujo' ? (
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="Cómo se desarrolla la clase">
              {flowProfileOptions.map((option) => {
                const selected = flowProfile === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-selected={selected ? 'true' : undefined}
                    className="studio-create-option flex w-full items-start gap-3 rounded-studio-xl p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-studio-accent-ring"
                    onClick={() => setFlowProfile(option.id)}
                  >
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-studio-md text-xl"
                      style={{
                        background: 'var(--studio-accent-subtle, #f5d4c8)',
                      }}
                      aria-hidden
                    >
                      {option.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-display text-sm font-bold text-studio-ink">
                        {option.label}
                      </span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-studio-ink-secondary">
                        {option.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="studio-create-modal__footer flex shrink-0 items-center justify-between gap-2 border-t border-studio-border bg-studio-surface-muted/50 px-6 py-4">
          <button
            type="button"
            className="studio-soft-btn-outline inline-flex items-center"
            onClick={() => (step === 'tipo' ? onOpenChange(false) : goBack())}
          >
            {step === 'tipo' ? (
              'Cancelar'
            ) : (
              <>
                <ChevronLeft className="mr-0.5 h-4 w-4" aria-hidden />
                Atrás
              </>
            )}
          </button>
          {step === 'flujo' ? (
            <button
              type="button"
              className="studio-create-modal__btn-primary"
              disabled={!canAdvance}
              onClick={handleComplete}
            >
              Generar material
            </button>
          ) : (
            <button
              type="button"
              className="studio-create-modal__btn-primary"
              disabled={!canAdvance}
              onClick={goNext}
            >
              Continuar
              <ChevronRight className="ml-0.5 h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
